import uFuzzy from '@leeoniya/ufuzzy';

const ufuzzy = new uFuzzy({
  intraMode: 1, // 0: MultiInsert, 1: SingleError
});

// Long search terms can cause recursion issues with the `ufuzzy` library
// Lowered from 80 to 50: https://chronosphereio.slack.com/archives/C08AZVDLVSR/p1774632061211329
const MAX_SEARCH_LENGTH = 50;

/**
 * Use the `ufuzzy` library to find metrics that match the search term.
 *
 * @param metrics The list of metrics that will be searched through.
 * @param searchTerm The search term that each metric will be compared to.
 */
export function getFuzzyMatchingMetrics(metrics: string[], searchTerm: string) {
  if (!metrics || metrics.length === 0) {
    return { matches: [] };
  }
  if (!searchTerm) {
    return { matches: metrics };
  }

  // For very long search terms, use simple substring matching to avoid recursion issues
  // https://github.com/leeoniya/uFuzzy/issues/65
  if (searchTerm.length > MAX_SEARCH_LENGTH) {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const matches = metrics.filter((metric) => metric.toLowerCase().includes(lowerSearchTerm));
    return { matches };
  }

  const haystack = metrics;
  const filteredIndices = ufuzzy.filter(haystack, searchTerm);

  if (!filteredIndices) {
    return { matches: [] };
  }

  const info = ufuzzy.info(filteredIndices, haystack, searchTerm);
  const infoOrder = ufuzzy.sort(info, haystack, searchTerm);

  const matches = [];
  const matchesHighlightedRanges = [];
  for (let i = 0; i < infoOrder.length; i++) {
    const infoIdx = infoOrder[i];
    if (infoIdx === undefined) {
      continue;
    }

    const haystackIdx = info.idx[infoIdx];
    if (haystackIdx === undefined) {
      continue;
    }

    const match = haystack[haystackIdx];
    if (match === undefined) {
      continue;
    }

    matches.push(match);
    matchesHighlightedRanges.push(info.ranges[infoIdx]);
  }

  return { matches, matchesHighlightedRanges };
}
